import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BookMarked, Printer } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { KpiManual } from "@/componentes/kpi/KpiPlanificacion"
import { ManualDocumento } from "@/componentes/manual/ManualDocumento"
import type { DocumentoManual, VersionManual } from "@/componentes/manual/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * El manual de organización: el borrador vivo y sus ediciones definitivas.
 *
 * ⚠ **El borrador no está guardado en ningún sitio: se arma al pedirlo.** Por eso
 * siempre dice la verdad de hoy. Guardarlo obligaría a mantenerlo sincronizado con
 * seis tablas y a contestar qué pasa cuando alguien edita un proceso sin volver a
 * generarlo — con la respuesta mala escrita en la base.
 *
 * ⚠ **Una edición definitiva es lo contrario, y por eso sí es una fila.** Congela el
 * documento entero. Un manual aprobado por la dirección no puede cambiar porque
 * alguien reordene un paso el mes que viene: eso es lo que un documento controlado
 * viene a impedir.
 *
 * ⚠ **La pantalla dice CUÁL se está mirando.** Sin ese aviso, quien abre una versión
 * congelada y ve un dato viejo cree que el sistema está desactualizado — y lo que pasa
 * es que está leyendo una foto de hace seis meses.
 */

type Respuesta = { documento: DocumentoManual; versiones: VersionManual[] }
type Detalle = VersionManual & { documento: DocumentoManual }

export function Manual() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedePublicar = tienePermiso(usuario, "manual:publicar")

  const [viendo, setViendo] = useState<number | null>(null)
  const [notas, setNotas] = useState("")

  const manual = useQuery({
    queryKey: ["manual"],
    queryFn: () => api.get<Respuesta>("/api/v1/manual"),
  })

  const version = useQuery({
    queryKey: ["manual", "version", viendo],
    queryFn: () => api.get<Detalle>(`/api/v1/manual/versiones/${viendo}`),
    enabled: viendo !== null,
  })

  const publicar = useMutation({
    mutationFn: () => api.post<VersionManual>("/api/v1/manual/versiones", { notas: notas || null }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["manual"] })
      setNotas("")
    },
  })

  const documento = viendo === null ? manual.data?.documento : version.data?.documento

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. */}
      <div className="no-print mb-4 flex justify-end">
        <Boton variante="secundario" onClick={() => window.print()}>
          <Printer size={16} aria-hidden="true" />
          Exportar PDF
        </Boton>
      </div>
      <KpiManual />

      <div className="no-print mb-6 flex flex-wrap items-center gap-2">
        <Boton
          variante={viendo === null ? "primario" : "fantasma"}
          tamano="sm"
          onClick={() => setViendo(null)}
        >
          Borrador vivo
        </Boton>
        {manual.data?.versiones.map((v) => (
          <Boton
            key={v.id}
            variante={viendo === v.id ? "primario" : "fantasma"}
            tamano="sm"
            onClick={() => setViendo(v.id)}
          >
            v{v.version}
          </Boton>
        ))}
      </div>

      {viendo !== null && (
        <p
          className="no-print mb-4 superficie px-3 py-2 text-sm"
          style={{ color: "var(--fg-muted)" }}
        >
          <Insignia tono="info">Edición congelada</Insignia> {(() => {
            const v = manual.data?.versiones.find((x) => x.id === viendo)
            return v
              ? `Publicada el ${comoFechaHora(v.creado_en)}${v.notas ? ` · ${v.notas}` : ""}. No refleja cambios posteriores.`
              : ""
          })()}
        </p>
      )}

      {puedePublicar && viendo === null && (
        <div className="no-print mb-6 flex flex-wrap items-end gap-2 superficie p-3">
          <div className="min-w-[220px] flex-1">
            <label htmlFor="notas-manual" className="mb-1 block text-xs font-medium uppercase">
              Congelar esta edición
            </label>
            <input
              id="notas-manual"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Qué cambia respecto a la anterior"
              maxLength={255}
              className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface)",
                color: "var(--fg)",
              }}
            />
          </div>
          <Boton
            variante="primario"
            onClick={() => publicar.mutate()}
            disabled={publicar.isPending}
          >
            {publicar.isPending ? "Publicando…" : "Publicar edición"}
          </Boton>
        </div>
      )}

      {publicar.isError && (
        <p role="alert" className="no-print mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {publicar.error instanceof ErrorApi ? publicar.error.message : "No se pudo publicar"}
        </p>
      )}

      {(manual.isPending || version.isPending) && viendo !== null && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando el manual…
        </p>
      )}

      {manual.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el manual
        </p>
      )}

      {manual.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Armando el manual…
        </p>
      )}

      {!documento && !manual.isPending && !manual.isError && (
        <div
          className="flex flex-col items-center gap-2 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <BookMarked size={22} aria-hidden="true" />
          <p className="text-sm">Todavía no hay nada que incluir en el manual</p>
        </div>
      )}

      {documento && (
        <div className="mx-auto max-w-[850px]">
          <ManualDocumento doc={documento} />
        </div>
      )}
    </div>
  )
}
